/**
 * DepositActions — per-gép kaució refund (teljes) + retain (visszatartás) + partial-refund
 * (parciális kár-levonás) + transfer-to-worksheet (szerviz-előleg átvezetés) akciók
 * (Slice A + Slice B + Slice C).
 *
 * A #684 deposit-row-ba integrálódik. Gating a deposit.refundStatus wire-érték alapján:
 *   refund / partial kínált: RECEIVED | HELD | RETAINED | PARTIAL
 *   retain kínált:           RECEIVED | HELD
 *   transfer kínált:         RECEIVED | HELD | RETAINED
 *   PENDING, RELEASED → NINCS akció (terminal/függő).
 *
 * A transfer-gating BE-verify (deposit-sale.service.ts `transferDepositToWorksheet` invariáns):
 *   `deposit.status !== HELD && !== COLLECTED && !== RETAINED` → ConflictException. A domain
 *   `COLLECTED` a Prisma/wire `RECEIVED`-ből térképeződik (deposit-mapping `statusMap`), így a
 *   wire-engedélyezett halmaz = { RECEIVED, HELD, RETAINED }. A PARTIAL (partially_retained)
 *   KIZÁRVA: az invariánson kívül esik + a `damageInvoiceId` cross-ág 409 (már kár-számlázott).
 *
 * DRY-money:
 *   - refund amount = deposit.amount (a FE NEM számol összeget, nincs szerkeszthető input).
 *   - partial: a user a `retainedAmount`-ot (kár) adja meg; a visszajáró CSAK input-segéd
 *     preview (deposit.amount − retainedAmount). A money-truth a BE `returnedAmount`.
 *   - transfer: az amount opcionális user-input (rész-átvezetés; üres = teljes). A money-truth
 *     a BE `transferredAmount` (a FE NEM számol átvezetett összeget).
 */
import type { RentalDeposit } from '@/api/rentals';
import { STATUS_LABEL_KEYS } from '@/api/worksheets';
import { useToast } from '@/hooks/use-toast';
import { useWorksheets } from '@/hooks/use-worksheets';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { depositActionErrorToastKey } from '../../../api/deposits';
import { useDepositActions } from '../hooks/use-deposit-actions';

interface DepositActionsProps {
  deposit: RentalDeposit;
  /** A bérlés partnere — a worksheet-picker `?partnerId=` szűrőjéhez (cross-partner kizárva). */
  customerId: string;
  onActionComplete: () => void;
}

const REFUND_STATUSES = new Set(['RECEIVED', 'HELD', 'RETAINED', 'PARTIAL']);
const RETAIN_STATUSES = new Set(['RECEIVED', 'HELD']);
/** Partial-refund (kár-levonás) ugyanabból a refund-halmazból kínált. */
const PARTIAL_STATUSES = new Set(['RECEIVED', 'HELD', 'RETAINED', 'PARTIAL']);
/**
 * Transfer-to-worksheet gating — a BE-invariáns wire-vetülete (lásd fejléc-komment):
 * { RECEIVED, HELD, RETAINED }. PARTIAL KIZÁRVA (már kár-számlázott / cross-ág 409).
 */
const TRANSFER_STATUSES = new Set(['RECEIVED', 'HELD', 'RETAINED']);
/** A worksheet-picker-ből KIZÁRT terminális munkalap-státuszok (BE R3-ratify: LEZART/TOROLVE → 409). */
const TRANSFER_EXCLUDED_WORKSHEET_STATUSES = new Set(['LEZART', 'TOROLVE']);
/**
 * RFC4122-STRICT UUID-szűrő: a nem-uuid worksheet.id-t kizárjuk a picker-ből (a BE @IsUUID 400 elkerülése).
 * Strict verzió ([1-5]) + variant ([89ab]) mező — a korábbi laza formátum-only regex átengedte a
 * `00000000-0000-0000-0002-000000000001` típusú seed-id-t (verzió/variant `0`), amit a BE @IsUUID elutasít
 * (dev4-verify-finding: a seed ML-2024-00001).
 */
const RFC4122_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** FE ÁFA-kulcs (number) → BE VatRate string. A BE-contract: RATE_27 | RATE_18 | RATE_5 | RATE_0. */
const VAT_RATE_TO_BE: Record<number, string> = {
  27: 'RATE_27',
  18: 'RATE_18',
  5: 'RATE_5',
  0: 'RATE_0',
};

/** ÁFA-kulcs opciók a partial-panel select-jéhez (a products/VAT_RATES tükre, dep-mentesen inline). */
const PARTIAL_VAT_OPTIONS: { value: number; label: string }[] = [
  { value: 27, label: '27%' },
  { value: 18, label: '18%' },
  { value: 5, label: '5%' },
  { value: 0, label: '0%' },
];

type ActivePanel = 'none' | 'refund' | 'retain' | 'partialRefund' | 'transfer';

export function DepositActions({ deposit, customerId, onActionComplete }: DepositActionsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { refund, retain, partialRefund, transferToWorksheet, loading } = useDepositActions();

  // React-hook-szabály: a worksheet-listát a komponens-body-ban hívjuk (NEM feltételesen a
  // panelben). A lista mindig betölt (partnerre szűrve); csak a transfer-panel jeleníti meg.
  const { worksheets, isLoading: worksheetsLoading } = useWorksheets({ partnerId: customerId });

  const [panel, setPanel] = useState<ActivePanel>('none');
  const [refundReason, setRefundReason] = useState('');
  const [retainReason, setRetainReason] = useState('');
  const [retainedAmount, setRetainedAmount] = useState('');
  const [damageDescription, setDamageDescription] = useState('');
  const [vatRate, setVatRate] = useState<number>(27);
  /** Sikeres partial után a kár-számla ID (link megjelenítés a panelben). */
  const [damageInvoiceId, setDamageInvoiceId] = useState<string | null>(null);
  /** Transfer-panel: kiválasztott cél-munkalap + opcionális rész-összeg. */
  const [selectedWorksheetId, setSelectedWorksheetId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  // Legacy / pre-#687-merge guard: depositId nélkül nincs cél-endpoint → ne renderelj akciót.
  const depositId = deposit.id;
  if (depositId == null) return null;

  const canRefund = REFUND_STATUSES.has(deposit.refundStatus);
  const canRetain = RETAIN_STATUSES.has(deposit.refundStatus);
  // 40-kör W2 (Javo-ACK): kártyás (CARD/PRE_AUTH) kaución a partial-refund a MyPOS-úton
  // megy, a BE 422-t ad → a CTA-t a FE eleve tiltja (BE-422 elé honesty, #662-minta).
  // A paymentMethod a #746 read-DTO-ból jön; hiányában (régi wire) nincs tiltás.
  const isCardDeposit = deposit.paymentMethod === 'CARD' || deposit.paymentMethod === 'PRE_AUTH';
  const canPartial = PARTIAL_STATUSES.has(deposit.refundStatus) && !isCardDeposit;
  const canTransfer = TRANSFER_STATUSES.has(deposit.refundStatus);
  if (!canRefund && !canRetain && !canPartial && !canTransfer && !isCardDeposit) return null;

  // Kliens-oldali whitelist: a terminális (LEZART/TOROLVE) munkalapok kizárva (BE R3-ratify 409).
  const transferableWorksheets = worksheets.filter(
    w => !TRANSFER_EXCLUDED_WORKSHEET_STATUSES.has(w.status) && RFC4122_UUID_RE.test(w.id)
  );

  const closePanel = () => {
    setPanel('none');
    setRefundReason('');
    setRetainReason('');
    setRetainedAmount('');
    setDamageDescription('');
    setVatRate(27);
    setDamageInvoiceId(null);
    setSelectedWorksheetId('');
    setTransferAmount('');
  };

  // --- partial-refund derived state (input-segéd, NEM money-truth) ---
  const retainedNum = Number.parseInt(retainedAmount, 10);
  const retainedValid = Number.isFinite(retainedNum) && retainedNum > 0;
  // Visszajáró-preview: CSAK UI segéd; a végleges a BE returnedAmount.
  const returnPreview = Math.max(0, deposit.amount - (retainedValid ? retainedNum : 0));
  const partialSubmitDisabled =
    loading || !retainedValid || retainedNum > deposit.amount || !damageDescription.trim();

  const handleRefundSubmit = async () => {
    try {
      await refund(depositId, {
        amount: deposit.amount,
        reason: refundReason || undefined,
      });
      toast({ title: t('rental.depositAction.refundSuccess'), variant: 'default' });
      closePanel();
      onActionComplete();
    } catch (err) {
      toast({ title: t(depositActionErrorToastKey(err)), variant: 'destructive' });
    }
  };

  const handleRetainSubmit = async () => {
    if (!retainReason.trim()) return;
    try {
      await retain(depositId, { reason: retainReason.trim() });
      toast({ title: t('rental.depositAction.retainSuccess'), variant: 'default' });
      closePanel();
      onActionComplete();
    } catch (err) {
      toast({ title: t(depositActionErrorToastKey(err)), variant: 'destructive' });
    }
  };

  const handlePartialSubmit = async () => {
    if (partialSubmitDisabled) return;
    try {
      const res = await partialRefund(depositId, {
        retainedAmount: retainedNum,
        damageDescription: damageDescription.trim(),
        vatRate: VAT_RATE_TO_BE[vatRate] ?? 'RATE_27',
      });
      const invoiceId = res.data.damageInvoiceId;
      // A toast description-je a kár-számla referenciát adja (a kattintható link a panelben jelenik meg).
      toast({
        title: t('rental.depositAction.partialRefundSuccess'),
        description: invoiceId ? t('rental.depositAction.viewDamageInvoice') : undefined,
        variant: 'default',
      });
      onActionComplete();
      if (invoiceId) {
        // Tartsuk meg a panelt a kár-számla linkkel; a többi mezőt nullázzuk.
        setDamageInvoiceId(invoiceId);
        setRetainedAmount('');
        setDamageDescription('');
        setVatRate(27);
      } else {
        closePanel();
      }
    } catch (err) {
      toast({ title: t(depositActionErrorToastKey(err)), variant: 'destructive' });
    }
  };

  // --- transfer-to-worksheet derived state ---
  const transferAmountNum = transferAmount === '' ? undefined : Number(transferAmount);
  const transferSubmitDisabled = loading || !selectedWorksheetId;

  const handleTransferSubmit = async () => {
    if (!selectedWorksheetId) return;
    try {
      await transferToWorksheet(depositId, {
        worksheetId: selectedWorksheetId,
        // amount üres = teljes nyitott kaució (a body amount-ot elhagyjuk); a transferredAmount BE-truth.
        amount: transferAmountNum,
      });
      toast({ title: t('rental.depositAction.transferSuccess'), variant: 'default' });
      closePanel();
      onActionComplete();
    } catch (err) {
      // 409 (már átvezetett / kár-számlázott / cross-partner / terminális munkalap) → invalidStatus toast.
      toast({ title: t(depositActionErrorToastKey(err)), variant: 'destructive' });
    }
  };

  return (
    <div className="mt-1" data-testid={`deposit-actions-${depositId}`}>
      {/* Akció-gombok */}
      {panel === 'none' && (
        <div className="flex gap-2">
          {canRefund && (
            <button
              type="button"
              data-testid={`deposit-action-refund-${depositId}`}
              onClick={() => setPanel('refund')}
              className="rounded-md border border-input px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
            >
              {t('rental.depositAction.refund')}
            </button>
          )}
          {canPartial && (
            <button
              type="button"
              data-testid={`deposit-action-partial-${depositId}`}
              onClick={() => setPanel('partialRefund')}
              className="rounded-md border border-input px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
            >
              {t('rental.depositAction.partialRefund')}
            </button>
          )}
          {/* W2: kártyás kaución a partial-refund CTA helyett honest MyPOS-jelzés */}
          {isCardDeposit && PARTIAL_STATUSES.has(deposit.refundStatus) && (
            <span
              data-testid={`deposit-partial-card-blocked-${depositId}`}
              title={t('rental.depositAction.cardPartialTooltip')}
              className="inline-flex items-center rounded-md border border-input px-2 py-1 text-xs text-muted-foreground opacity-70"
            >
              {t('rental.depositAction.cardPartialBlocked')}
            </span>
          )}
          {canRetain && (
            <button
              type="button"
              data-testid={`deposit-action-retain-${depositId}`}
              onClick={() => setPanel('retain')}
              className="rounded-md border border-input px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
            >
              {t('rental.depositAction.retain')}
            </button>
          )}
          {canTransfer && (
            <button
              type="button"
              data-testid={`deposit-action-transfer-${depositId}`}
              onClick={() => setPanel('transfer')}
              className="rounded-md border border-input px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
            >
              {t('rental.depositAction.transfer')}
            </button>
          )}
        </div>
      )}

      {/* 41-kör FE-3 ②: a „miért tiltva" magyarázat LÁTHATÓ mikromásolatként is —
          a pult touch-kijelzőjén a title-tooltip nem elérhető (hover nincs). */}
      {panel === 'none' && isCardDeposit && PARTIAL_STATUSES.has(deposit.refundStatus) && (
        <p
          className="mt-1 text-xs text-muted-foreground"
          data-testid={`deposit-partial-card-why-${depositId}`}
        >
          {t('rental.depositAction.cardPartialTooltip')}
        </p>
      )}

      {/* Refund megerősítő panel — amount NEM szerkeszthető (teljes visszatérítés) */}
      {panel === 'refund' && (
        <div
          className="mt-1 space-y-2 rounded-md border border-input p-3"
          data-testid={`deposit-refund-panel-${depositId}`}
        >
          <p className="text-xs font-medium text-foreground">
            {t('rental.depositAction.refundTitle')}
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid={`deposit-refund-amount-${depositId}`}
          >
            {t('rental.deposit')}: {formatCurrency(deposit.amount)}
          </p>
          <input
            type="text"
            data-testid={`deposit-refund-reason-${depositId}`}
            value={refundReason}
            onChange={e => setRefundReason(e.target.value)}
            placeholder={t('rental.depositAction.reasonPlaceholder')}
            aria-label={t('rental.depositAction.reason')}
            className="w-full rounded-md border border-input px-2 py-1 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closePanel}
              disabled={loading}
              className="flex-1 rounded-md border border-input px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('rental.depositAction.cancel')}
            </button>
            <button
              type="button"
              data-testid={`deposit-refund-submit-${depositId}`}
              onClick={() => void handleRefundSubmit()}
              disabled={loading}
              className="flex-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
            >
              {t('rental.depositAction.submit')}
            </button>
          </div>
        </div>
      )}

      {/* Partial-refund panel — kár-levonás: retainedAmount + damageDescription + vatRate KÖTELEZŐ */}
      {panel === 'partialRefund' && (
        <div
          className="mt-1 space-y-2 rounded-md border border-input p-3"
          data-testid={`deposit-partial-panel-${depositId}`}
        >
          <p className="text-xs font-medium text-foreground">
            {t('rental.depositAction.partialRefundTitle')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('rental.deposit')}: {formatCurrency(deposit.amount)}
          </p>

          <label className="block text-xs text-muted-foreground">
            {t('rental.depositAction.retainedAmount')}
            <input
              type="number"
              data-testid={`deposit-partial-amount-${depositId}`}
              value={retainedAmount}
              onChange={e => setRetainedAmount(e.target.value)}
              min={1}
              max={deposit.amount}
              placeholder={t('rental.depositAction.retainedAmountPlaceholder')}
              aria-label={t('rental.depositAction.retainedAmount')}
              className="mt-1 w-full rounded-md border border-input px-2 py-1 text-sm text-foreground"
            />
          </label>

          <label className="block text-xs text-muted-foreground">
            {t('rental.depositAction.damageDescription')}
            <textarea
              data-testid={`deposit-partial-description-${depositId}`}
              value={damageDescription}
              onChange={e => setDamageDescription(e.target.value)}
              maxLength={400}
              placeholder={t('rental.depositAction.damageDescriptionPlaceholder')}
              aria-label={t('rental.depositAction.damageDescription')}
              className="mt-1 w-full rounded-md border border-input px-2 py-1 text-sm text-foreground"
            />
          </label>

          <label className="block text-xs text-muted-foreground">
            {t('rental.depositAction.vatRate')}
            <select
              data-testid={`deposit-partial-vat-${depositId}`}
              value={vatRate}
              onChange={e => setVatRate(Number.parseInt(e.target.value, 10))}
              aria-label={t('rental.depositAction.vatRate')}
              className="mt-1 w-full rounded-md border border-input px-2 py-1 text-sm text-foreground"
            >
              {PARTIAL_VAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          {/* Visszajáró-preview — input-segéd, NEM money-truth (a végleges a BE returnedAmount). */}
          <p
            className="text-sm font-medium text-foreground"
            data-testid={`deposit-partial-return-preview-${depositId}`}
          >
            {t('rental.depositAction.returnPreview')}: {formatCurrency(returnPreview)}
          </p>

          {/* Sikeres partial után a kár-számla kattintható link. */}
          {damageInvoiceId && (
            <Link
              to={`/invoices/${damageInvoiceId}`}
              data-testid={`deposit-partial-invoice-link-${depositId}`}
              className="block text-sm font-medium text-primary underline"
            >
              {t('rental.depositAction.viewDamageInvoice')}
            </Link>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={closePanel}
              disabled={loading}
              className="flex-1 rounded-md border border-input px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('rental.depositAction.cancel')}
            </button>
            <button
              type="button"
              data-testid={`deposit-partial-submit-${depositId}`}
              onClick={() => void handlePartialSubmit()}
              disabled={partialSubmitDisabled}
              className="flex-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
            >
              {t('rental.depositAction.submit')}
            </button>
          </div>
        </div>
      )}

      {/* Retain panel — reason KÖTELEZŐ (üres → disabled submit) */}
      {panel === 'retain' && (
        <div
          className="mt-1 space-y-2 rounded-md border border-input p-3"
          data-testid={`deposit-retain-panel-${depositId}`}
        >
          <p className="text-xs font-medium text-foreground">
            {t('rental.depositAction.retainTitle')}
          </p>
          <input
            type="text"
            data-testid={`deposit-retain-reason-${depositId}`}
            value={retainReason}
            onChange={e => setRetainReason(e.target.value)}
            placeholder={t('rental.depositAction.retainReasonPlaceholder')}
            aria-label={t('rental.depositAction.reason')}
            className="w-full rounded-md border border-input px-2 py-1 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closePanel}
              disabled={loading}
              className="flex-1 rounded-md border border-input px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('rental.depositAction.cancel')}
            </button>
            <button
              type="button"
              data-testid={`deposit-retain-submit-${depositId}`}
              onClick={() => void handleRetainSubmit()}
              disabled={loading || !retainReason.trim()}
              className="flex-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
            >
              {t('rental.depositAction.submit')}
            </button>
          </div>
        </div>
      )}

      {/* Transfer-to-worksheet panel — worksheet-picker (partner-szűrt, terminális kizárva) + opcionális rész-összeg */}
      {panel === 'transfer' && (
        <div
          className="mt-1 space-y-2 rounded-md border border-input p-3"
          data-testid={`deposit-transfer-panel-${depositId}`}
        >
          <p className="text-xs font-medium text-foreground">
            {t('rental.depositAction.transferTitle')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('rental.deposit')}: {formatCurrency(deposit.amount)}
          </p>

          {worksheetsLoading ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid={`deposit-transfer-loading-${depositId}`}
            >
              {t('rental.depositAction.worksheetLoading')}
            </p>
          ) : transferableWorksheets.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid={`deposit-transfer-empty-${depositId}`}
            >
              {t('rental.depositAction.noWorksheets')}
            </p>
          ) : (
            <>
              <label className="block text-xs text-muted-foreground">
                {t('rental.depositAction.worksheet')}
                <select
                  data-testid={`deposit-transfer-worksheet-${depositId}`}
                  value={selectedWorksheetId}
                  onChange={e => setSelectedWorksheetId(e.target.value)}
                  aria-label={t('rental.depositAction.worksheet')}
                  className="mt-1 w-full rounded-md border border-input px-2 py-1 text-sm text-foreground"
                >
                  <option value="">{t('rental.depositAction.worksheetPlaceholder')}</option>
                  {transferableWorksheets.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.worksheetNumber} — {t(STATUS_LABEL_KEYS[w.status])}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-xs text-muted-foreground">
                {t('rental.depositAction.transferAmount')}
                <input
                  type="number"
                  data-testid={`deposit-transfer-amount-${depositId}`}
                  value={transferAmount}
                  onChange={e => setTransferAmount(e.target.value)}
                  min={1}
                  max={deposit.amount}
                  placeholder={t('rental.depositAction.transferAmountPlaceholder')}
                  aria-label={t('rental.depositAction.transferAmount')}
                  className="mt-1 w-full rounded-md border border-input px-2 py-1 text-sm text-foreground"
                />
              </label>
            </>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={closePanel}
              disabled={loading}
              className="flex-1 rounded-md border border-input px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('rental.depositAction.cancel')}
            </button>
            <button
              type="button"
              data-testid={`deposit-transfer-submit-${depositId}`}
              onClick={() => void handleTransferSubmit()}
              disabled={transferSubmitDisabled}
              className="flex-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
            >
              {t('rental.depositAction.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
